// LandingPage.js — landing 100% orientée app mobile.
// Aucun lien vers l'app web (login/signup retirés volontairement) :
// les anciens utilisateurs passent par /login en direct.
import React, { useState } from 'react';
import './LandingPage.css'
import Navbar, { PLAY_STORE_URL } from './Navbar';
import Footer from './Footer';

const FAQ_ITEMS = [
    {
        q: 'What is Echonote?',
        a: "Echonote is a voice notes app for your phone. You record yourself talking: ideas, tasks, meeting debriefs, anything. The AI turns the recording into a clean, structured note, a to-do list, or any custom format you define. No typing, no re-listening.",
    },
    {
        q: "How is it different from my phone's voice recorder?",
        a: "Your phone's recorder gives you an audio file. To use it, you still have to re-listen and type everything out yourself. Echonote gives you the finished result: a formatted note you can read, edit, and share seconds after you stop talking.",
    },
    {
        q: 'What can I turn a recording into?',
        a: "Three things, out of the box: structured notes with headings and bullet points, to-do lists with checkable tasks, and custom styles. Custom styles are your own instructions, like 'summarize this as a client email' or 'turn this into a blog outline'. Every recording processed with that style comes back formatted that way.",
    },
    {
        q: 'How accurate is the transcription?',
        a: "Echonote uses state-of-the-art AI speech recognition that handles natural, casual speech: half-sentences, hesitations, and filler words included. It supports more than 50 languages, so you can speak the way that comes naturally.",
    },
    {
        q: 'Is Echonote free?',
        a: "Yes, for real. Echonote is free to download and free to use: recording, transcription, and notes work without paying anything. Some advanced features are reserved for the premium plan, and the app tells you exactly which ones before you spend a cent.",
    },
    {
        q: 'Is my data safe?',
        a: "Yes. Your recordings and notes are protected with industry-standard encryption, stored securely, and never shared with third parties without your explicit consent.",
    },
    {
        q: 'What languages does Echonote support?',
        a: 'Echonote transcribes more than 50 languages, including English, Spanish, French, German, Portuguese, Italian, Japanese, and Chinese. Speak in the language you think in.',
    },
    {
        q: 'Where can I download Echonote?',
        a: 'Echonote is available on Android, on the Google Play Store. Install it, hit record, and see what your voice memos should have looked like all along.',
    },
];

const LandingPage = () => {

    const [openIndex, setOpenIndex] = useState(null);

    const togglePanel = (index) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    const PlayBadge = () => (
        <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer">
            <img width="220" src="/images/playstore-badge.png" alt="Get Echonote on Google Play" />
        </a>
    );

    return (
        <>
            <Navbar />

            <div className="landing-page">

                {/* ── Hero ─────────────────────────────────────────── */}
                <div className="section hero-section">
                    <div className="hero-content">
                        <h4>Voice notes, organized by AI</h4>
                        <h1>Talk for a minute. Get a note <span className="neo-mark">worth keeping.</span></h1>
                        <p>
                            Echonote records your rambling, cuts the noise, and hands back a clean note,
                            a to-do list, or any format you ask for. Right in your pocket.
                        </p>

                        <div className="hero-cta">
                            <a className="button-important no-style-link" href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer">
                                <span className="inner">
                                    <span className="material-symbols-outlined icon">smartphone</span>
                                    Get Echonote free
                                </span>
                            </a>
                            <p className="cta-note">Free on Google Play · Premium unlocks more</p>
                        </div>

                        <div className="featured-badge">
                            <PlayBadge />
                            <a href="https://theresanaiforthat.com/ai/echonote/?ref=featured&v=139162" target="_blank" rel="noopener noreferrer nofollow">
                                <img width="220" src="https://media.theresanaiforthat.com/featured-on-taaft.png?width=600" alt="Echonote featured on There's An AI For That" />
                            </a>
                        </div>
                    </div>

                    <div className="hero-image">
                        <img src="/illustrations/hero-phone.png" alt="Echonote voice notes app on a phone" />
                    </div>
                </div>

                {/* ── Problème ─────────────────────────────────────── */}
                <div className="band">
                    <div className="section problem-section">
                        <div className="section-header">
                            <h4>The problem</h4>
                            <h2>Voice memos are where good ideas <span className="neo-underline">go to die</span>.</h2>
                        </div>
                        <p>
                            Recording takes 30 seconds. Dealing with it is the hard part: re-listen,
                            scrub back, type it out, clean it up. Fifteen minutes for one memo.
                        </p>
                        <p>
                            So you don't. The idea sits in <em>"New recording 47"</em> forever. A week
                            later, it's gone.
                        </p>
                        <p className="problem-kicker">
                            Echonote does the dealing-with part for you, in about the time it takes to
                            lock your phone.
                        </p>
                    </div>
                </div>

                {/* ── Features ─────────────────────────────────────── */}
                <div className="section features-section" id="features">
                    <div className="section-header">
                        <h4>Features</h4>
                        <h2>One recording. <span className="neo-underline">Any kind of note.</span></h2>
                        <p>
                            Echonote isn't a recorder with a transcript bolted on. It's the step
                            between your voice and something you can actually use.
                        </p>
                    </div>

                    <div className="features-grid">
                        <div className="feature-item">
                            <div className="icon">
                                <span className="material-symbols-outlined">notes</span>
                            </div>
                            <div>
                                <h4>Speak in fragments, get a clean note</h4>
                                <p>You talk the way you think: half-sentences, tangents, ums. Echonote returns headings, bullets, and full sentences you can actually reuse.</p>
                            </div>
                        </div>

                        <div className="feature-item">
                            <div className="icon">
                                <span className="material-symbols-outlined">checklist</span>
                            </div>
                            <div>
                                <h4>Say it, then check it off</h4>
                                <p>List five errands while you're driving. When you open the app, they're already a checklist, each one waiting for its checkmark.</p>
                            </div>
                        </div>

                        <div className="feature-item">
                            <div className="icon">
                                <span className="material-symbols-outlined">palette</span>
                            </div>
                            <div>
                                <h4>Your words, in any format</h4>
                                <p>Meeting recap, blog draft, message to a client: write a style prompt once and every recording comes back formatted exactly that way.</p>
                            </div>
                        </div>

                        <div className="feature-item">
                            <div className="icon">
                                <span className="material-symbols-outlined">smartphone</span>
                            </div>
                            <div>
                                <h4>Made for where ideas happen</h4>
                                <p>In the car, on a walk, between two meetings. Your phone is already in your pocket. Now your note-taker is too.</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Comment ça marche ────────────────────────────── */}
                <div className="band">
                    <div className="section steps-section" id="how-it-works">
                    <div className="image-container">
                        <img src="/illustrations/how-it-works-vertical.png" alt="From a voice recording to an organized note" className="steps-image" />
                    </div>
                    <div className="steps-content">
                        <div className='section-header'>
                            <h4>How it works</h4>
                            <h2>From ramble to note in <span className="neo-mark">three steps</span></h2>
                        </div>
                        <div className="step">
                            <h3 className="step-number">01</h3>
                            <div className="step-details">
                                <h4 className="step-title">Hit record and talk</h4>
                                <p className="step-description">No structure needed. Say it the way it comes.</p>
                            </div>
                        </div>
                        <div className="step">
                            <h3 className="step-number">02</h3>
                            <div className="step-details">
                                <h4 className="step-title">The AI cleans up</h4>
                                <p className="step-description">Transcription, filler removal, formatting: all done in the background while you move on.</p>
                            </div>
                        </div>
                        <div className="step">
                            <h3 className="step-number">03</h3>
                            <div className="step-details">
                                <h4 className="step-title">Use it right away</h4>
                                <p className="step-description">Edit, copy, or share a note that reads like you wrote it carefully. You didn't. That's the point.</p>
                            </div>
                        </div>
                    </div>
                    </div>
                </div>

                {/* ── Cas d'usage ──────────────────────────────────── */}
                <div className="section usecases-section" id="use-cases">
                    <div className="section-header">
                        <h4>Use cases</h4>
                        <h2>Who's talking to <span className="neo-mark">Echonote</span>?</h2>
                    </div>
                    <div className="usecases-grid">
                        <div className="usecase-item">
                            <span className="material-symbols-outlined usecase-icon">school</span>
                            <h4>Students</h4>
                            <p>Lecture recaps and revision ideas, structured before you've left the classroom.</p>
                        </div>
                        <div className="usecase-item">
                            <span className="material-symbols-outlined usecase-icon">work</span>
                            <h4>Busy professionals</h4>
                            <p>Meeting debriefs turned into action items before you're back at your desk.</p>
                        </div>
                        <div className="usecase-item">
                            <span className="material-symbols-outlined usecase-icon">draw</span>
                            <h4>Creators</h4>
                            <p>Shower thoughts captured as outlines and drafts before they evaporate.</p>
                        </div>
                        <div className="usecase-item">
                            <span className="material-symbols-outlined usecase-icon">home</span>
                            <h4>Everyday life</h4>
                            <p>Groceries, gift ideas, errands: said once, never forgotten.</p>
                        </div>
                    </div>
                </div>

                {/* ── FAQ ──────────────────────────────────────────── */}
                <div className="band">
                    <div className="section faq-section" id="faq">
                    <div className="section-header">
                        <h4>FAQ</h4>
                        <h2>Questions, <span className="neo-underline">answered</span></h2>
                    </div>

                    <div className="faq-grid">
                        {FAQ_ITEMS.map((item, index) => (
                            <div className="faq-item" key={index}>
                                <div className="faq-question" onClick={() => togglePanel(index)}>
                                    <span>{item.q}</span>
                                    <span className="material-symbols-outlined icon">
                                        {openIndex === index ? 'expand_circle_up' : 'expand_circle_down'}
                                    </span>
                                </div>
                                {openIndex === index && (
                                    <div className="faq-answer">{item.a}</div>
                                )}
                            </div>
                        ))}
                    </div>
                    </div>
                </div>

                {/* ── CTA final ────────────────────────────────────── */}
                <div className="section final-cta-section">
                    <div className="final-cta-card">
                        <div className="final-cta-text">
                            <h2>Your next idea deserves better than <em>"New recording 47"</em>.</h2>
                            <p>
                                Put Echonote on your phone now. The next time inspiration hits,
                                you'll be ready. Thirty seconds later, it'll be a note.
                            </p>
                            <div className="hero-cta">
                                <a className="button-important no-style-link" href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer">
                                    <span className="inner">
                                        <span className="material-symbols-outlined icon">download</span>
                                        Download Echonote
                                    </span>
                                </a>
                                <p className="cta-note">Free download · AI-organized notes in seconds</p>
                            </div>
                        </div>
                        <div className="final-cta-image">
                            <img src="/illustrations/cta-done.png" alt="A finished checklist in Echonote" />
                        </div>
                    </div>
                </div>

                <Footer />
            </div>
        </>
    );
};

export default LandingPage;
